<template>
  <button
    class="rpb"
    :class="[`rpb-${visualState}`, { 'rpb-compact': compact }]"
    :disabled="loading"
    :title="tooltip"
    type="button"
    @click="$emit('refresh')"
  >
    <i class="fa" :class="iconClass"></i>
    <span class="rpb-label">{{ label }}</span>
  </button>
</template>

<script>
/**
 * Refresh-from-Open-Dental control, shared by the Builder top bar and Present
 * Mode's topbar actions.
 *
 * ONE component rather than a copy per mode, because the two have to agree on
 * what a refresh means and what it just did. The `compact` prop is the only
 * difference: the Builder carries the sync age inline, Present Mode (where a
 * patient can see the screen) says as little as possible.
 *
 * Palette resolves through a fallback chain — `--txb-*` inside the Builder,
 * `--pm-*` inside Present Mode, hardcoded defaults if neither is present — so
 * the button inherits whichever theme it's mounted in without either page
 * needing to know it exists.
 */
import { describeRefreshOutcome } from '@/pages/TxPresenter/utils/refreshDiff.js';

const RESULT_FLASH_MS = 4000;
const AGE_TICK_MS = 30000;

export default {
  name: 'RefreshPlanButton',

  props: {
    /** Mirror of builder.loading — spins the icon and blocks re-entry. */
    loading: { type: Boolean, default: false },
    /** Mirror of builder.lastSyncAt (client-side fetch stamp, NOT freshness). */
    lastSyncAt: { type: Number, default: null },
    /** Mirror of builder.refreshOutcome — { added, removed, at } or null. */
    outcome: { type: Object, default: null },
    /** Timestamp of the last failed refresh; parent sets it in its catch. */
    failedAt: { type: Number, default: null },
    /** Present Mode variant: shorter labels, no sync age. */
    compact: { type: Boolean, default: false },
  },

  data() {
    return {
      now: Date.now(),
      flash: null, // null | 'result' | 'error'
      flashTimer: null,
      tickTimer: null,
    };
  },

  computed: {
    visualState() {
      if (this.loading) return 'loading';
      return this.flash || 'idle';
    },

    iconClass() {
      if (this.loading) return 'fa-refresh fa-spin';
      if (this.flash === 'error') return 'fa-exclamation-triangle';
      if (this.flash === 'result') return 'fa-check';
      return 'fa-refresh';
    },

    /**
     * Idle is just "Refresh".
     *
     * It used to read "Refresh · checked 4m ago", which duplicated the tooltip
     * below verbatim and was the sole reason this button reserved 196px. A TC
     * doesn't decide anything from "checked 4m ago" — they click Refresh
     * because they just added something in Open Dental. The outcome flash
     * ("2 procedures added") stays: that one IS decision-relevant, and it's
     * what the reserved width is now sized for.
     */
    label() {
      if (this.loading) return 'Checking…';
      if (this.flash === 'error') return this.compact ? 'Failed' : 'Refresh failed';
      if (this.flash === 'result') return describeRefreshOutcome(this.outcome);
      return 'Refresh';
    },

    tooltip() {
      // Deliberately "checked", never "synced". lastSyncAt is stamped in this
      // browser when the fetch returned — it says nothing about whether the
      // site's exe has actually replicated a just-added procedure yet.
      return 'Pull the latest procedures from Open Dental'
        + (this.lastSyncAt ? ` · last checked ${this.relTime(this.lastSyncAt)}` : '');
    },
  },

  watch: {
    'outcome.at': function onOutcome(at) {
      if (at) this.startFlash('result');
    },
    failedAt(at) {
      if (at) this.startFlash('error');
    },
  },

  mounted() {
    // Keeps "checked 10m ago" honest without a render loop.
    this.tickTimer = setInterval(() => { this.now = Date.now(); }, AGE_TICK_MS);
  },

  beforeDestroy() {
    if (this.tickTimer) clearInterval(this.tickTimer);
    if (this.flashTimer) clearTimeout(this.flashTimer);
  },

  methods: {
    startFlash(kind) {
      if (this.flashTimer) clearTimeout(this.flashTimer);
      this.flash = kind;
      this.flashTimer = setTimeout(() => {
        this.flash = null;
        this.flashTimer = null;
      }, RESULT_FLASH_MS);
    },

    relTime(ms) {
      const diff = Math.max(0, this.now - ms);
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      return `${Math.floor(hrs / 24)}d ago`;
    },
  },
};
</script>

<style lang="scss" scoped>
.rpb {
  --rpb-surface: var(--txb-surface, var(--pm-surface, #ffffff));
  --rpb-surface2: var(--txb-surface2, #f1f3f6);
  --rpb-border: var(--txb-border, var(--pm-border, #e4e7ec));
  --rpb-text-mute: var(--txb-text-mute, var(--pm-text-mute, #475569));
  --rpb-accent: var(--txb-accent, var(--pm-accent, #0f6e63));
  --rpb-accent-soft: var(--txb-accent-soft, var(--pm-accent-soft, #e6f1ef));

  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  border: 1px solid var(--rpb-border);
  background: var(--rpb-surface);
  color: var(--rpb-text-mute);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.rpb .fa { font-size: 12px; }

.rpb:hover:not(:disabled) {
  background: var(--rpb-surface2);
  border-color: var(--rpb-accent);
  color: var(--rpb-accent);
}
.rpb:disabled { cursor: default; }

/* Loading — muted, not alarming. Nothing has gone wrong. */
.rpb-loading {
  color: var(--rpb-text-mute);
  opacity: 0.8;
}

/* Result — the whole point of the control. Tinted enough to catch the eye
   in a busy top bar, quiet enough not to read as an alert in front of a
   patient in Present Mode. */
.rpb-result {
  background: var(--rpb-accent-soft);
  border-color: var(--rpb-accent);
  color: var(--rpb-accent);
  font-weight: 600;
}

.rpb-error {
  background: #fffbeb;
  border-color: #fcd34d;
  color: #92400e;
  font-weight: 600;
}

/* Reserved width, same reasoning as .txb-save-indicator: this sits upstream of
   the Builder's action cluster in a flex row, so an unreserved label change
   shoves Comments / Send / Present sideways. Now sized for the widest state
   that remains — the outcome flash ("2 procedures added") — rather than for an
   idle label carrying a sync age that duplicated the tooltip. 150px, was 196.
   The Builder overrides this inside .txb-sync-cluster; the compact (Present
   Mode) variant keeps a short stable label and needs no reservation. */
.rpb:not(.rpb-compact) { min-width: 150px; justify-content: flex-start; }

.rpb-compact { padding: 8px 14px; font-weight: 600; }
.rpb-compact.rpb-idle { color: var(--rpb-accent); }

/* Match the touch targets Present Mode already sets for Print / Exit. */
@media (max-width: 1024px) {
  .rpb-compact { min-height: 44px; padding: 10px 16px; }
}
</style>
